<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { ProductFeature } from '@/api/generated/api.enum';
import CloudUploadIcon from '@/assets/icons/CloudUploadIcon.vue';
import ImageIcon from '@/assets/icons/ImageIcon.vue';
import RemoveIcon from '@/assets/icons/RemoveIcon.vue';
import { useBillingState } from '@/composables/billing/useBillingState';
import { useFileDropZone } from '@/composables/useFileDropZone';
import BillingProBadge from '@/core/billing/BillingProBadge.vue';
import { EFileUploadError } from 'common/enum/file.upload.enum';
import { computed, onScopeDispose, ref, shallowRef, useTemplateRef } from 'vue';
import { APPEARANCE_IMAGE_MAX_BYTES, APPEARANCE_IMAGE_TYPES } from '../appearance.constants';
import { useAppearanceImageUrl } from '@/composables/useEntityAppearance';

const { t } = useI18n();
const { hasFeature } = useBillingState();
const isLocked = computed(() => !hasFeature(ProductFeature.tenantCustomization));

const props = defineProps<{ source?: File | string }>();
const emit = defineEmits<{ select: [file: File]; remove: []; 'request-upgrade': [] }>();
const imageUrl = useAppearanceImageUrl(() => props.source);
const dropZone = useTemplateRef<HTMLElement>('dropZone');
const input = useTemplateRef<HTMLInputElement>('input');
const error = ref('');
const pendingImage = shallowRef<HTMLImageElement | null>(null);
const loading = computed(() => pendingImage.value !== null);

const clearPendingImage = (): void => {
  const image = pendingImage.value;
  if (!image) return;
  image.onload = null;
  image.onerror = null;
  URL.revokeObjectURL(image.src);
  image.removeAttribute('src');
  pendingImage.value = null;
};
onScopeDispose(clearPendingImage);

const selectFile = (file: File): void => {
  if (isLocked.value) {
    emit('request-upgrade');
    return;
  }
  clearPendingImage();
  error.value = '';
  const image = new Image();
  image.onload = () => {
    clearPendingImage();
    if (hasFeature(ProductFeature.tenantCustomization)) emit('select', file);
  };
  image.onerror = () => {
    clearPendingImage();
    error.value = t('entityAppearance.imageDecodeError');
  };
  pendingImage.value = image;
  image.src = URL.createObjectURL(file);
};
const remove = (): void => {
  clearPendingImage();
  error.value = '';
  emit('remove');
};
const { accept, isOverDropZone, openFileExplorer, handleFileChange } = useFileDropZone({
  dropZoneRef: dropZone,
  fileInputRef: input,
  allowedFileTypes: APPEARANCE_IMAGE_TYPES,
  maxSizeBytes: APPEARANCE_IMAGE_MAX_BYTES,
  isBlocked: () => isLocked.value || loading.value,
  onBlockedIntent: () => {
    if (isLocked.value) emit('request-upgrade');
  },
  onFile: selectFile,
  onError: (reason) => {
    clearPendingImage();
    error.value =
      reason === EFileUploadError.SIZE ? t('entityAppearance.imageSizeError') : t('entityAppearance.imageTypeError');
  },
});
</script>

<template>
  <div class="flex min-h-0 flex-col overflow-y-auto overscroll-contain p-md">
    <input ref="input" type="file" :accept="accept" class="hidden" @change="handleFileChange" />
    <button
      ref="dropZone"
      type="button"
      class="relative flex aspect-square w-full shrink-0 max-md:aspect-auto max-md:min-h-0 max-md:flex-1 items-center justify-center overflow-hidden rounded-2xl border border-dashed border-divider outline-inner"
      :class="[isOverDropZone && !isLocked && '!border-tint bg-highlight', imageUrl && '!border-solid']"
      :aria-label="t('entityAppearance.selectImage')"
      :aria-busy="loading"
      @click="openFileExplorer"
    >
      <img v-if="imageUrl" :src="imageUrl" alt="" class="size-full object-cover" />
      <span v-else class="flex flex-col items-center gap-md text-body leading-body text-secondary">
        <ImageIcon class="size-icon" aria-hidden="true" />
        <span class="text-center"
          >{{ t('entityAppearance.dropImage') }}<br /><span class="underline underline-offset-4">{{
            t('entityAppearance.selectImage')
          }}</span></span
        >
        <BillingProBadge v-if="isLocked" theme="gray" />
      </span>
    </button>
    <p v-if="loading" role="status" class="pt-smd text-footer text-secondary">
      {{ t('entityAppearance.imageLoading') }}
    </p>
    <p v-if="error" role="alert" class="pt-smd text-footer text-error">{{ error }}</p>
    <div class="shrink-0 pt-md">
      <button
        type="button"
        class="state-effects flex min-h-[48px] w-full items-center gap-smd rounded-sm px-sm py-sm text-body leading-body text-primary outline-inner"
        @click="openFileExplorer"
      >
        <CloudUploadIcon class="size-icon shrink-0" />{{ t('entityAppearance.uploadImage') }}
        <BillingProBadge v-if="isLocked" theme="dark" class="ml-auto shrink-0" />
      </button>
      <div class="mx-sm my-sm border-t border-divider"></div>
      <button
        type="button"
        class="state-effects flex min-h-[48px] w-full items-center gap-smd rounded-sm px-sm py-sm text-body leading-body text-error outline-inner disabled:pointer-events-none disabled:text-secondary disabled:opacity-50"
        :disabled="!source && !loading"
        @click="remove"
      >
        <RemoveIcon class="size-icon shrink-0" />{{ t('entityAppearance.removeImage') }}
      </button>
    </div>
  </div>
</template>
